import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { performanceAPI } from '../lib/api';
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Target,
  Calendar,
  Award,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MONTHS, PERFORMANCE_PARAMS, getScoreColor, getPerformanceLabelColor, getRiskLabelColor } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, BarChart, Bar, Legend } from 'recharts';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PerformanceDetail = () => {
  const { designerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [downloadingPeriod, setDownloadingPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [awards, setAwards] = useState([]);
  const reportRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await performanceAPI.getDesigner(designerId, selectedYear);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching designer performance:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [designerId, selectedYear]);

  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const token = localStorage.getItem('arrise_token');
        const res = await axios.get(`${BACKEND_URL}/api/awards/designer/${designerId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAwards(res.data.awards || []);
      } catch (err) {
        console.error('Failed to load awards:', err);
      }
    };
    fetchAwards();
  }, [designerId]);

  const handleExportPDF = async () => {
    if (!reportRef.current || !data) return;
    
    setExporting(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#F6F6F6'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      // Add header
      pdf.setFillColor(51, 16, 98);
      pdf.rect(0, 0, pdfWidth, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.text('ARRISE Performance Report', 14, 16);
      
      // Add designer name and date
      pdf.setFontSize(10);
      pdf.text(`${data.designer.name} - ${data.designer.role}`, pdfWidth - 14, 10, { align: 'right' });
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pdfWidth - 14, 16, { align: 'right' });
      
      // Add image
      pdf.addImage(imgData, 'PNG', imgX, 30, imgWidth * ratio * 0.95, imgHeight * ratio * 0.95);
      
      // Save
      pdf.save(`${data.designer.name.replace(/\s+/g, '_')}_Performance_Report_${selectedYear}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const downloadPeriodReport = async (months, format = 'pdf') => {
    if (!data) return;
    const key = `${months}-${format}`;
    setDownloadingPeriod(key);
    try {
      const token = localStorage.getItem('arrise_token');
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth(), 1);
      const start = new Date(end.getFullYear(), end.getMonth() - (months - 1), 1);

      const endpoint = format === 'pdf'
        ? `${BACKEND_URL}/api/reports/member-performance/pdf`
        : `${BACKEND_URL}/api/reports/member-performance/excel`;

      const response = await axios.get(endpoint, {
        params: {
          designer_id: designerId,
          start_year: start.getFullYear(),
          start_month: start.getMonth() + 1,
          end_year: end.getFullYear(),
          end_month: end.getMonth() + 1,
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.setAttribute(
        'download',
        `${data.designer.name.replace(/\s+/g, '_')}_${months}M_Performance.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Period report download failed:', error);
      const msg = error?.response?.status === 404
        ? 'No scores found for this period'
        : 'Failed to download report';
      alert(msg);
    } finally {
      setDownloadingPeriod(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-slate-800">Designer not found</h3>
        <Button asChild className="mt-4">
          <Link to="/performance">Go Back</Link>
        </Button>
      </div>
    );
  }

  const { designer, target, monthly_data, all_scores } = data;

  // Prepare monthly trend data (all 6 months)
  const trendData = monthly_data.map(m => ({
    name: MONTHS[m.month - 1]?.substring(0, 3),
    score: m.avg_health ?? 0,
    target: target.target_avg_health,
    month: m.month,
    year: m.year
  })).reverse();

  // Prepare radar data from latest scores
  const latestScore = all_scores[0];
  const radarData = latestScore ? PERFORMANCE_PARAMS.map(p => ({
    subject: p.label.split(' ')[0],
    score: latestScore[p.key],
    fullMark: 5,
  })) : [];

  // Prepare monthly breakdown chart data
  const monthlyBreakdown = monthly_data.slice(0, 6).map(m => ({
    name: `${MONTHS[m.month - 1]?.substring(0, 3)} ${m.year}`,
    avgHealth: m.avg_health ?? 0,
    feedback: m.total_positive ?? 0,
    leaves: m.total_leaves ?? 0,
    escalations: m.total_escalations ?? 0
  })).reverse();

  // Calculate current month stats
  const currentMonth = monthly_data[0];
  const avgHealth = currentMonth?.avg_health ?? 0;
  const performanceLabel = avgHealth >= target.target_avg_health + 0.3 
    ? 'Growth Track (High Performer)'
    : avgHealth >= target.target_avg_health 
    ? 'On Track'
    : avgHealth >= target.target_avg_health - 0.3
    ? 'Needs Improvement'
    : 'At Risk';

  const riskLabel = avgHealth < target.kra_target_min 
    ? 'High Risk' 
    : avgHealth < target.target_avg_health 
    ? 'Medium Risk' 
    : 'Low Risk';

  // Calculate 6-month average
  const validMonths = monthly_data.filter(m => m.avg_health !== undefined && m.avg_health !== null);
  const sixMonthAvg = validMonths.length > 0 
    ? (validMonths.reduce((sum, m) => sum + (m.avg_health || 0), 0) / validMonths.length).toFixed(2)
    : '0.00';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="performance-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/performance" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 font-outfit">
              {designer.name}
            </h1>
            <p className="text-slate-500">
              {designer.role} • {designer.vertical_team}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => downloadPeriodReport(6, 'pdf')}
            disabled={downloadingPeriod === '6-pdf'}
            variant="outline"
            className="border-rose-500 text-rose-600 hover:bg-rose-50"
            data-testid="download-6m-pdf-btn"
          >
            {downloadingPeriod === '6-pdf' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            6-Month PDF
          </Button>
          <Button
            onClick={() => downloadPeriodReport(12, 'pdf')}
            disabled={downloadingPeriod === '12-pdf'}
            variant="outline"
            className="border-rose-500 text-rose-600 hover:bg-rose-50"
            data-testid="download-12m-pdf-btn"
          >
            {downloadingPeriod === '12-pdf' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            12-Month PDF
          </Button>
          <Button
            onClick={() => downloadPeriodReport(6, 'xlsx')}
            disabled={downloadingPeriod === '6-xlsx'}
            variant="outline"
            size="sm"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="download-6m-btn"
          >
            {downloadingPeriod === '6-xlsx' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            6M xlsx
          </Button>
          <Button
            onClick={() => downloadPeriodReport(12, 'xlsx')}
            disabled={downloadingPeriod === '12-xlsx'}
            variant="outline"
            size="sm"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="download-12m-btn"
          >
            {downloadingPeriod === '12-xlsx' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            12M xlsx
          </Button>
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            className="bg-[#331062] hover:bg-[#2A0D52]"
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
        </div>
      </div>

      {/* Award badges */}
      {awards.length > 0 && (
        <div
          className="flex flex-wrap gap-2 px-1"
          data-testid="designer-award-badges"
        >
          {awards.map((a, i) => {
            const isStar = a.type === 'star_of_quarter';
            const label = isStar
              ? `Star of Q${a.quarter} ${a.year}`
              : `Best Performer ${a.year}`;
            return (
              <Link
                key={`${a.type}-${a.year}-${a.quarter || 'y'}-${i}`}
                to="/awards"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shadow-sm hover:shadow-md transition-shadow ${
                  isStar
                    ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 text-purple-800 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                }`}
                title={`${label} · ${a.scope} · Composite ${a.composite_score}`}
              >
                <Award className="w-3.5 h-3.5" />
                {label}
                <span className="opacity-70">· {a.scope}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Printable Report Section */}
      <div ref={reportRef} className="space-y-6 bg-[#F6F6F6] p-4">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="stat-card-primary">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-white/70">Current KRA</p>
                    <p className="text-2xl font-bold text-white mt-1 font-outfit">
                      {(avgHealth ?? 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-white/60 mt-1">
                      Target: {target.kra_target_min}
                    </p>
                  </div>
                  <Target className="w-5 h-5 text-white/70" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">6-Month Avg</p>
                <p className={`text-2xl font-bold mt-1 font-outfit ${getScoreColor(parseFloat(sixMonthAvg))}`}>
                  {sixMonthAvg}
                </p>
                <p className="text-xs text-slate-400 mt-1">Overall trend</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Performance</p>
                <div className="mt-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPerformanceLabelColor(performanceLabel)}`}>
                    {performanceLabel.includes('High') ? 'High Performer' : performanceLabel}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Risk Level</p>
                <div className="mt-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRiskLabelColor(riskLabel)}`}>
                    {riskLabel}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardContent className="p-5">
                <div className="grid grid-cols-3 gap-1 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Feedback</p>
                    <p className="text-lg font-bold text-emerald-600">
                      {monthly_data.reduce((sum, m) => sum + (m.total_positive || 0), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Leaves</p>
                    <p className="text-lg font-bold text-amber-600">
                      {monthly_data.reduce((sum, m) => sum + (m.total_leaves || 0), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Esc.</p>
                    <p className="text-lg font-bold text-red-600">
                      {monthly_data.reduce((sum, m) => sum + (m.total_escalations || 0), 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 6-Month Trend Chart */}
          <Card className="chart-container">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#331062]" />
                6-Month Performance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                    <YAxis domain={[0, 5]} stroke="#64748B" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#331062"
                      strokeWidth={3}
                      dot={{ fill: '#331062' }}
                      name="Avg Health"
                    />
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="#CCFF00"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Target"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Skills Radar Chart */}
          <Card className="chart-container">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <Award className="w-5 h-5 text-[#331062]" />
                Skills Breakdown (Latest Week)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#E2E8F0" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#331062"
                        fill="#331062"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    No scores available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown Bar Chart */}
        <Card className="chart-container">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#331062]" />
              Monthly Performance Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="avgHealth" name="Avg Health" fill="#331062" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="feedback" name="Positive Feedback" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="escalations" name="Escalations" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Scores Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#331062]" />
              Weekly Score History ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th>Period</th>
                    <th>Quality</th>
                    <th>Brief</th>
                    <th>Speed</th>
                    <th>Guidelines</th>
                    <th>Discipline</th>
                    <th>Avg</th>
                    <th>Escalation</th>
                  </tr>
                </thead>
                <tbody>
                  {all_scores.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500">
                        No score history available for {selectedYear}
                      </td>
                    </tr>
                  ) : (
                    all_scores.slice(0, 24).map((score, index) => (
                      <motion.tr
                        key={score.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                      >
                        <td className="font-medium text-sm">
                          {MONTHS[score.month - 1]?.substring(0, 3)} W{score.week}
                        </td>
                        <td className={getScoreColor(score.quality_consistency)}>{score.quality_consistency}</td>
                        <td className={getScoreColor(score.brief_understanding)}>{score.brief_understanding}</td>
                        <td className={getScoreColor(score.speed_vs_accuracy)}>{score.speed_vs_accuracy}</td>
                        <td className={getScoreColor(score.guideline_adherence)}>{score.guideline_adherence}</td>
                        <td className={getScoreColor(score.team_discipline)}>{score.team_discipline}</td>
                        <td>
                          <span className={`font-semibold ${getScoreColor(score.avg_health)}`}>
                            {(score.avg_health ?? 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          {score.escalation === 'Major' ? (
                            <span className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded-full">Major</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Target Reference */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              <Target className="w-5 h-5 text-[#331062]" />
              Role Targets ({designer.role})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">Target Health</p>
                <p className="text-xl font-bold text-slate-800">{target.target_avg_health}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">Target Monthly</p>
                <p className="text-xl font-bold text-slate-800">{target.target_overall_monthly}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">KRA Min</p>
                <p className="text-xl font-bold text-slate-800">{target.kra_target_min}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">Max Escalations</p>
                <p className="text-xl font-bold text-slate-800">{target.max_major_escalations}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">Min Feedback</p>
                <p className="text-xl font-bold text-slate-800">{target.min_positive_feedback}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500">Max Leaves</p>
                <p className="text-xl font-bold text-slate-800">{target.max_unplanned_leaves}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceDetail;
